/**
 * EmbedSnippetGenerator — copy-paste iframe / SDK snippet builder for every
 * embeddable INZARA surface (content embeds + admin panels).
 *
 * The developer picks a surface, fills in the host/workspace/resource id, and
 * gets two ready-to-paste snippets:
 *   • an <iframe> using the legacy `?token=` fallback (drop-in for any stack)
 *   • a React `<InzaraEmbed>` snippet (postMessage token bridge, auto-refresh)
 *
 * No real secrets are ever inserted — token fields are always placeholders the
 * host swaps for a short-lived JWT minted server-side via
 * POST /api/mgmt/v1/embed/sessions (content) or a delegated admin-session token.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Boxes, Copy, Check, Code2, Globe,
  FileText, Gauge, LayoutDashboard, BarChart3, Sparkles, Activity, Search, Store,
  Network, Users, KeyRound, Bell, DollarSign, ToggleRight, ShieldCheck,
} from 'lucide-react';
import { useBrand } from '@/lib/branding';

// Per-surface icon + accent for the visual thumbnail picker.
const SURFACE_META = {
  insights: { icon: FileText, accent: 'text-sky-300' },
  'kpi-monitor': { icon: Gauge, accent: 'text-emerald-300' },
  dashboard: { icon: LayoutDashboard, accent: 'text-cyan-300' },
  report: { icon: BarChart3, accent: 'text-cyan-300' },
  explore: { icon: Sparkles, accent: 'text-violet-300' },
  activity: { icon: Activity, accent: 'text-pink-300' },
  investigate: { icon: Search, accent: 'text-amber-300' },
  marketplace: { icon: Store, accent: 'text-orange-300' },
  'er-diagram': { icon: Network, accent: 'text-teal-300' },
  'user-management': { icon: Users, accent: 'text-amber-300' },
  governance: { icon: ShieldCheck, accent: 'text-amber-300' },
  identity: { icon: KeyRound, accent: 'text-amber-300' },
  webhooks: { icon: Bell, accent: 'text-amber-300' },
  usage: { icon: DollarSign, accent: 'text-amber-300' },
  'feature-flags': { icon: ToggleRight, accent: 'text-amber-300' },
};
const KIND_BADGE = { content: 'bg-primary/15 text-primary', view: 'bg-cyan-500/15 text-cyan-300', admin: 'bg-amber-500/15 text-amber-300' };

function SurfaceThumb({ surface, selected, onSelect }) {
  const meta = SURFACE_META[surface.value] || { icon: Boxes, accent: 'text-muted-foreground' };
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={() => onSelect(surface.value)}
      className={`group relative flex flex-col items-center gap-1.5 p-2 rounded-lg border transition-all ${
        selected ? 'border-primary/60 bg-primary/10 ring-1 ring-primary/30' : 'border-border/40 bg-background/40 hover:border-border/70'
      }`}
      data-testid={`snippet-thumb-${surface.value}`}
      title={surface.label}
    >
      {/* mini browser-frame mock */}
      <div className="w-full aspect-[16/10] rounded-md bg-gradient-to-br from-muted/40 to-background/60 border border-border/30 flex items-center justify-center overflow-hidden">
        <div className="absolute top-1 left-1.5 flex gap-0.5">
          <span className="w-1 h-1 rounded-full bg-border/70" />
          <span className="w-1 h-1 rounded-full bg-border/70" />
          <span className="w-1 h-1 rounded-full bg-border/70" />
        </div>
        <Icon className={`w-6 h-6 ${meta.accent}`} strokeWidth={1.5} />
      </div>
      <span className="text-[9px] text-center leading-tight text-foreground/90 line-clamp-2">{surface.label}</span>
      <span className={`text-[8px] px-1 rounded ${KIND_BADGE[surface.kind] || 'bg-muted text-muted-foreground'}`}>{surface.kind}</span>
    </button>
  );
}

// Fallback catalog if the backend surface registry can't be reached.
const DEFAULT_SURFACES = [
  // Content embeds — short-lived embed-session JWT (POST /embed/sessions)
  { value: 'insights',    label: 'Insights',        kind: 'content', needsId: false },
  { value: 'kpi-monitor', label: 'KPI Monitor',     kind: 'content', needsId: false },
  { value: 'dashboard',   label: 'Dashboard',       kind: 'view',    needsId: true  },
  { value: 'report',      label: 'Report',          kind: 'view',    needsId: true  },
  { value: 'explore',     label: 'AI Explore',      kind: 'content', needsId: false },
  { value: 'activity',    label: 'Activity Feed',   kind: 'content', needsId: false },
  { value: 'investigate', label: 'Investigate',     kind: 'content', needsId: false },
  { value: 'marketplace', label: 'Marketplace',     kind: 'content', needsId: false },
  { value: 'er-diagram',  label: 'ER Diagram',      kind: 'content', needsId: false },
  // Admin panels — delegated admin-session token
  { value: 'user-management', label: 'Admin · User Management', kind: 'admin', needsId: false },
  { value: 'governance',      label: 'Admin · Governance',      kind: 'admin', needsId: false },
  { value: 'identity',        label: 'Admin · Identity',        kind: 'admin', needsId: false },
  { value: 'webhooks',        label: 'Admin · Webhooks',        kind: 'admin', needsId: false },
  { value: 'usage',           label: 'Admin · Usage & Billing', kind: 'admin', needsId: false },
  { value: 'feature-flags',   label: 'Admin · Feature Flags',   kind: 'admin', needsId: false },
];

function CopyBtn({ text, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }
        catch { /* clipboard blocked — noop */ }
      }}
      className="text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
      data-testid={testid}
    >
      {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function embedPath(surface, resourceId) {
  if (surface.kind === 'view') return `/embed/v2/view/${surface.value}/${resourceId || '<id>'}`;
  if (surface.kind === 'admin') return `/embed/v2/admin/${surface.value}`;
  return `/embed/v2/${surface.value}`;
}

export default function EmbedSnippetGenerator({ apiBase, bearerKey }) {
  const brand = useBrand();
  const [expanded,    setExpanded]    = useState(false);
  const [surfaces,    setSurfaces]    = useState(DEFAULT_SURFACES);
  const [surfaceVal,  setSurfaceVal]  = useState('insights');
  const [host,        setHost]        = useState(apiBase || '');
  const [resourceId,  setResourceId]  = useState('');
  const [height,      setHeight]      = useState(640);
  const [originsWs,   setOriginsWs]   = useState('');
  const [originsList, setOriginsList] = useState(null); // null=unknown, []=any, [..]=restricted
  const [originsMsg,  setOriginsMsg]  = useState('');
  const [previewUrl,  setPreviewUrl]  = useState('');

  // Live check: is `previewUrl`'s origin allowed to embed this workspace?
  const previewResult = useMemo(() => {
    if (originsList === null || !previewUrl.trim()) return null;
    let origin;
    try { origin = new URL(previewUrl.trim()).origin; } catch { return { ok: false, invalid: true }; }
    if (originsList.length === 0) return { ok: true, origin, any: true };
    return { ok: originsList.includes(origin), origin };
  }, [originsList, previewUrl]);

  const checkOrigins = async () => {
    const wid = originsWs.trim();
    if (!wid) return;
    const key = bearerKey?.trim();
    setOriginsMsg('Loading…'); setOriginsList(null);
    try {
      const res = await fetch(`${apiBase}/api/mgmt/v1/workspaces/${encodeURIComponent(wid)}`, {
        // Pasted mgmt key when present; otherwise ride the logged-in admin's
        // session cookie so signed-in admins can peek without pasting a key.
        headers: key ? { Authorization: `Bearer ${key}` } : {},
        credentials: key ? 'same-origin' : 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setOriginsList(data.allowed_origins || []);
      setOriginsMsg('');
    } catch (e) {
      setOriginsMsg(String(e.message || e));
      setOriginsList(null);
    }
  };

  // Pull the live surface registry so new backend panels appear automatically.
  useEffect(() => {
    let alive = true;
    fetch(`${apiBase}/api/mgmt/v1/embed/surfaces`)
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (!alive || !body?.surfaces?.length) return;
        setSurfaces(body.surfaces.map((s) => ({
          value: s.value, label: s.label, kind: s.kind, needsId: !!s.needs_id,
        })));
      })
      .catch(() => { /* keep DEFAULT_SURFACES */ });
    return () => { alive = false; };
  }, [apiBase]);

  const surface = useMemo(() => surfaces.find((s) => s.value === surfaceVal) || surfaces[0], [surfaces, surfaceVal]);
  const tokenLabel = surface.kind === 'admin' ? '<DELEGATED_ADMIN_SESSION_TOKEN>' : '<SHORT_LIVED_EMBED_JWT>';
  const path = embedPath(surface, resourceId);
  const cleanHost = (host || '').replace(/\/$/, '');

  const iframeSnippet =
`<!-- Drop-in iframe. Mint the token server-side; never expose a mgmt key in the browser. -->
<iframe
  src="${cleanHost}${path}?token=${tokenLabel}"
  width="100%"
  height="${height}"
  frameborder="0"
  allow="clipboard-write"
  title="${brand.name} · ${surface.label}"
></iframe>`;

  const reactProps = surface.kind === 'admin'
    ? `  panel="${surface.value}"`
    : `  resource="${surface.value}"${surface.needsId ? `\n  id="${resourceId || '<id>'}"` : ''}`;

  const reactSnippet =
`import { ${brand.embedComponent} } from '${brand.packageScope}/embed-react';

<${brand.embedComponent}
  host="${cleanHost}"
  token={/* ${surface.kind === 'admin' ? 'delegated admin-session token' : 'JWT from /api/mgmt/v1/embed/sessions'} */}
${reactProps}
  height={${height}}
  onEvent={(ev) => console.log(ev)}
  onTokenRefresh={(next) => saveForNextMount(next)}
/>`;

  const mintHint = surface.kind === 'admin'
    ? 'Token: mint a delegated admin-session token (POST /api/mgmt/v1/admin/sessions) scoped to this panel.'
    : 'Token: mint a short-lived embed JWT server-side (POST /api/mgmt/v1/embed/sessions).';

  return (
    <div className="rounded-lg border border-border/40 bg-muted/10 mb-6" data-testid="developers-snippet-generator">
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="developers-snippet-generator-toggle"
      >
        <div className="flex items-center gap-2">
          <Boxes className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Embed Snippet Generator</span>
          <span className="text-[10px] text-muted-foreground">iframe + React SDK for any surface</span>
        </div>
        <span className="text-[10px] text-muted-foreground">{expanded ? 'Hide' : 'Show'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border/40 pt-3">
          {/* Visual surface picker — pick a surface by its thumbnail. */}
          <div>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Pick a surface</span>
            <div className="mt-1.5 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="developers-snippet-thumbs">
              {surfaces.map((s) => (
                <SurfaceThumb key={s.value} surface={s} selected={s.value === surfaceVal} onSelect={setSurfaceVal} />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
            <label className="space-y-1 md:col-span-2">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Surface</span>
              <select
                value={surfaceVal}
                onChange={(e) => setSurfaceVal(e.target.value)}
                className="w-full px-2 py-1.5 rounded border border-border/40 bg-background/60"
                data-testid="developers-snippet-surface"
              >
                <optgroup label="Content embeds">
                  {surfaces.filter((s) => s.kind !== 'admin').map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </optgroup>
                <optgroup label="Admin panels">
                  {surfaces.filter((s) => s.kind === 'admin').map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </optgroup>
              </select>
            </label>
            <label className={`space-y-1 ${surface.needsId ? '' : 'opacity-40'}`}>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Resource id</span>
              <input
                value={resourceId}
                onChange={(e) => setResourceId(e.target.value)}
                placeholder={surface.needsId ? 'dash_… / rpt_…' : 'not required'}
                disabled={!surface.needsId}
                spellCheck={false}
                className="w-full font-mono px-2 py-1.5 rounded border border-border/40 bg-background/60"
                data-testid="developers-snippet-resource-id"
              />
            </label>
            <label className="space-y-1">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Height (px)</span>
              <input
                type="number"
                value={height}
                min={200}
                onChange={(e) => setHeight(Math.max(200, Number(e.target.value) || 640))}
                className="w-full font-mono px-2 py-1.5 rounded border border-border/40 bg-background/60"
                data-testid="developers-snippet-height"
              />
            </label>
          </div>

          <label className="block space-y-1 text-xs">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Host</span>
            <input
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder={`https://your-${brand.slug}-host.com`}
              spellCheck={false}
              className="w-full font-mono px-2 py-1.5 rounded border border-border/40 bg-background/60"
              data-testid="developers-snippet-host"
            />
          </label>

          {/* Allowed origins peek — shows the host which sites may mount this
              workspace's embeds, so they see the restriction while copying. */}
          <div className="rounded border border-emerald-500/25 bg-emerald-500/[0.04] px-3 py-2" data-testid="snippet-origins">
            <div className="flex items-center gap-2 mb-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-[11px] font-medium">Allowed embed origins</span>
              <span className="text-[10px] text-muted-foreground">— which sites may mount this workspace</span>
            </div>
            <div className="flex gap-2">
              <input
                value={originsWs}
                onChange={(e) => setOriginsWs(e.target.value)}
                placeholder="workspace_id (e.g. ws_…)"
                spellCheck={false}
                className="flex-1 font-mono text-[11px] px-2 py-1.5 rounded border border-border/40 bg-background/60"
                data-testid="snippet-origins-workspace"
              />
              <button
                type="button"
                onClick={checkOrigins}
                disabled={!originsWs.trim()}
                className="text-[11px] px-3 py-1.5 rounded border border-border/40 bg-muted/30 disabled:opacity-40"
                data-testid="snippet-origins-check"
              >
                Check origins
              </button>
            </div>
            {!bearerKey?.trim() && (
              <p className="text-[10px] text-muted-foreground mt-1.5" data-testid="snippet-origins-nokey">
                Uses your pasted API key, or your signed-in admin session if you&rsquo;re logged in.
              </p>
            )}
            {originsMsg && (
              <p className="text-[10px] text-rose-400 mt-1.5" data-testid="snippet-origins-error">{originsMsg}</p>
            )}
            {originsList !== null && !originsMsg && (
              originsList.length === 0 ? (
                <p className="text-[10px] text-muted-foreground mt-1.5" data-testid="snippet-origins-any">
                  No allowlist configured — this workspace&rsquo;s embeds can be mounted from <span className="text-amber-300">any site</span>.
                </p>
              ) : (
                <ul className="mt-1.5 space-y-0.5" data-testid="snippet-origins-list">
                  {originsList.map((o) => (
                    <li key={o} className="font-mono text-[10px] text-emerald-300/90">• {o}</li>
                  ))}
                </ul>
              )
            )}

            {originsList !== null && !originsMsg && (
              <div className="mt-2 pt-2 border-t border-border/30" data-testid="origin-preview">
                <div className="text-[10px] text-muted-foreground mb-1">Test a site — is it allowed to embed this workspace?</div>
                <input
                  value={previewUrl}
                  onChange={(e) => setPreviewUrl(e.target.value)}
                  placeholder="https://app.acme.com/dashboard"
                  spellCheck={false}
                  className="w-full font-mono text-[11px] px-2 py-1.5 rounded border border-border/40 bg-background/60"
                  data-testid="origin-preview-input"
                />
                {previewResult && (
                  previewResult.invalid ? (
                    <p className="text-[10px] text-rose-400 mt-1" data-testid="origin-preview-result">Not a valid URL.</p>
                  ) : previewResult.ok ? (
                    <p className="text-[10px] text-emerald-300 mt-1" data-testid="origin-preview-result">
                      ✓ <span className="font-mono">{previewResult.origin}</span> {previewResult.any ? 'is allowed (no allowlist set).' : 'is on the allowlist.'}
                    </p>
                  ) : (
                    <p className="text-[10px] text-rose-400 mt-1" data-testid="origin-preview-result">
                      ✕ <span className="font-mono">{previewResult.origin}</span> is blocked — not on the allowlist.
                    </p>
                  )
                )}
              </div>
            )}
          </div>

          <p className="text-[11px] text-amber-300/90">{mintHint} The snippets below use placeholder token values — swap them at runtime.</p>

          <div className="rounded border border-border/40 bg-background/60" data-testid="developers-snippet-iframe-block">
            <div className="flex items-center justify-between px-2 py-1 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border/30">
              <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" /> iframe</span>
              <CopyBtn text={iframeSnippet} testid="developers-snippet-copy-iframe" />
            </div>
            <pre className="text-[11px] px-3 py-2 overflow-x-auto font-mono whitespace-pre">{iframeSnippet}</pre>
          </div>

          <div className="rounded border border-border/40 bg-background/60" data-testid="developers-snippet-react-block">
            <div className="flex items-center justify-between px-2 py-1 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border/30">
              <span className="inline-flex items-center gap-1"><Code2 className="w-3 h-3" /> React · {brand.packageScope}/embed-react</span>
              <CopyBtn text={reactSnippet} testid="developers-snippet-copy-react" />
            </div>
            <pre className="text-[11px] px-3 py-2 overflow-x-auto font-mono whitespace-pre">{reactSnippet}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
